/**
 * Copyright 2025 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Modified by the CHAP-R1 project from the original Google Appoint-Ready source;
 * see the NOTICE file in the repository root.
 */

import React, { useState, useEffect } from 'react';
import PatientBuilder from './components/PatientBuilder/PatientBuilder'; // Updated import path
import Interview from './components/Interview/Interview';
import HeadlessDashboard from './components/HeadlessDashboard/HeadlessDashboard';
import Login from './components/Login/Login';
import PreloadImages from './components/PreloadImages';

const baseURL = window.location.hostname === 'localhost' ? '' : '';

const App = () => {
  const [currentPage, setCurrentPage] = useState('login');
  const [patientName, setPatientName] = useState('');

  // Training mode state (detected from URL ?mode=training)
  const [isTrainingMode, setIsTrainingMode] = useState(false);
  // Headless mode state (detected from URL ?mode=headless)
  const [isHeadlessMode, setIsHeadlessMode] = useState(false);
  // Patient mode state (detected from URL ?mode=patient)
  const [isPatientMode, setIsPatientMode] = useState(false);
  const [isPrimaryCareMode, setIsPrimaryCareMode] = useState(false);
  // HTN refill mode (detected from URL /htn) - full clinician UI, HTN prompts
  const [isHtnMode, setIsHtnMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseData, setCaseData] = useState(null);

  // Detect training/headless mode from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const pathname = window.location.pathname.toLowerCase();

    if (mode === 'training') {
      // Show password modal instead of immediately enabling training mode
      setShowPasswordModal(true);
    } else if (mode === 'headless') {
      // Enable headless mode (HeadlessDashboard handles its own password)
      setIsHeadlessMode(true);
    } else if (mode === 'patient' || pathname === '/patient') {
      setIsPatientMode(true);
    } else if (mode === 'primarycare' || pathname === '/primarycare') {
      setIsPatientMode(true);
      setIsPrimaryCareMode(true);
    } else if (mode === 'htn' || pathname === '/htn') {
      // Full clinician UI (like base ED mode), but HTN refill agent prompts
      setIsHtnMode(true);
    }
  }, []);

  const handlePasswordSubmit = async () => {
    try {
      const response = await fetch(`${baseURL}/api/validate_training_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });

      const data = await response.json();
      if (data.valid) {
        setIsTrainingMode(true);
        setShowPasswordModal(false);
        setPasswordError('');
        // Bypass main login for training/headless modes
        setCurrentPage('patientBuilder');
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (error) {
      setPasswordError('Error validating password');
    }
  };

  const handleSwitchToInterview = () => {
    setCurrentPage('interview');
  };

  const imageList = [
    '/assets/ai_headshot.svg',
    '/assets/Mike.png',
    '/assets/MikeTorso.png',
    '/assets/generic_patient.png'
  ];

  // If in headless mode, render HeadlessDashboard (handles own auth)
  if (isHeadlessMode) {
    return <HeadlessDashboard />;
  }

  return (
    <PreloadImages imageSources={imageList}>
      {/* Password Modal for Training Mode */}
      {showPasswordModal && (
        <div className="password-modal-overlay">
          <div className="training-password-modal" style={{ height: 'auto', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>Training Mode Access</h2>
            <p>Enter password to access training mode:</p>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              placeholder="Password"
              autoFocus
            />
            {passwordError && <p className="password-error">{passwordError}</p>}
            <div className="password-modal-buttons">
              <button onClick={() => {
                setShowPasswordModal(false);
                // Redirect to main mode without training
                window.history.replaceState({}, '', window.location.pathname);
              }}>
                Cancel
              </button>
              <button onClick={handlePasswordSubmit} className="primary">
                Enter
              </button>
            </div>
          </div>
        </div>
      )}

      {currentPage === 'login' ? (
        <Login onLogin={() => setCurrentPage('patientBuilder')} />
      ) : currentPage === 'patientBuilder' ? (
        <PatientBuilder
          patientName={patientName}
          setPatientName={setPatientName}
          onNext={handleSwitchToInterview}
          isTrainingMode={isTrainingMode}
          isPatientMode={isPatientMode}
          isPrimaryCareMode={isPrimaryCareMode}
          selectedCase={selectedCase}
          setSelectedCase={setSelectedCase}
          caseData={caseData}
          setCaseData={setCaseData}
        />
      ) : currentPage === 'interview' ? (
        <Interview
          patientName={patientName}
          onBack={() => setCurrentPage('patientBuilder')}
          isTrainingMode={isTrainingMode}
          isPatientMode={isPatientMode}
          isPrimaryCareMode={isPrimaryCareMode}
          isHtnMode={isHtnMode}
          caseData={caseData}
        />
      ) : null}
    </PreloadImages>
  );
};

export default App;
